import { useState, useEffect, Suspense, useMemo } from 'react';
import SkeletonCard from './dashboard-skeleton-card';
import DashboardCard from './dashboard-card';
import { DASHBOARD_COLORS } from './dashboard-colors';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { useConversationsOverview } from '../dashboard.hook';
import { DashboardFilters } from '../dashboard.types';

interface ConversationChartProps {
  startDate?: string;
  endDate?: string;
  platform?: string;
}

export default function ConversationChart({
  startDate,
  endDate,
  platform,
}: ConversationChartProps) {
  const [chartOptions, setChartOptions] = useState({});
  const [chartSeries, setChartSeries] = useState<
    { name: string; data: number[] }[]
  >([]);
  const apiFilters: DashboardFilters = useMemo(() => {
    const medium =
      platform === 'WhatsApp'
        ? 'whatsapp'
        : platform === 'Web'
          ? 'web'
          : undefined;
    return {
      start_date: startDate || new Date().toISOString().split('T')[0],
      end_date: endDate || new Date().toISOString().split('T')[0],
      medium,
    };
  }, [startDate, endDate, platform]);

  const { data, isLoading } = useConversationsOverview(apiFilters, true);

  useEffect(() => {
    if (isLoading) return;

    const points = data?.points ?? [];

    const chartOptions: ApexOptions = {
      chart: {
        id: 'area-chart',
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      legend: {
        position: 'top',
        horizontalAlign: 'center',
        floating: false,
        fontSize: '14px',
        fontWeight: 500,

        markers: {
          shape: 'circle',
          size: 10,
        },
      },
      xaxis: {
        type: 'category',
        categories: points.map((item) => {
          const date = new Date(item.date);
          return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
          });
        }),
        labels: {
          rotate: -45,
          rotateAlways: true,
          hideOverlappingLabels: true,
          trim: true,
          maxHeight: 120,
          style: {
            fontSize: '10px',
          },
          offsetY: 5,
        },
        tickAmount: 15,
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        title: {
          text: 'Conversations',
          style: {
            fontSize: '12px',
            fontWeight: 500,
            color: DASHBOARD_COLORS.neutral,
          },
        },
        min: 0,
        labels: {
          formatter: function (value: number) {
            return Math.round(value).toString();
          },
          style: {
            fontSize: '12px',
          },
        },
      },
      colors: [DASHBOARD_COLORS.whatsapp, DASHBOARD_COLORS.web],
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.2,
          stops: [0, 90, 100],
        },
      },
      tooltip: {
        x: {
          show: true,
        },
        theme: 'light',
      },
      markers: {
        size: 5,
        strokeWidth: 2,
        strokeColors: '#fff',
        hover: {
          size: 7,
        },
      },
      grid: {
        borderColor: '#f1f5f9',
        strokeDashArray: 3,
      },
    };

    // Prepare data for the chart
    const chartData = {
      options: chartOptions,
      series: [
        {
          name: 'WhatsApp',
          data: points.map((item) => Number(item.whatsapp || 0)),
        },
        {
          name: 'Web',
          data: points.map((item) => Number(item.web || 0)),
        },
      ],
    };

    setChartOptions(chartData.options);
    setChartSeries(chartData.series);
  }, [isLoading, data, endDate, startDate, platform]);

  if (isLoading) {
    return <SkeletonCard />;
  }

  return (
    <DashboardCard title="Conversations Overview">
      <Suspense fallback={<SkeletonCard />}>
        <Chart
          options={chartOptions}
          series={chartSeries}
          type="area"
          height={350}
        />
      </Suspense>
    </DashboardCard>
  );
}
